import { sedrApi, type SedrStarterSetDetail } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';

export function showSedrStarterSetPreviewDialog(options: {
  setId: string;
  setName: string;
}): void {
  showSedrStarterSetImportDialog({
    ...options,
    mode: 'preview',
  });
}

export function showSedrStarterSetImportDialog(options: {
  roomId?: string;
  setId: string;
  setName: string;
  onDone?: () => void;
  mode?: 'import' | 'preview';
}): void {
  const mode = options.mode ?? 'import';
  const isPreview = mode === 'preview';

  const shell = createModalShell({
    titleKey: isPreview ? 'sedr.import.frameworkPreviewTitle' : 'sedr.import.frameworkSelectTitle',
    titleId: isPreview ? 'sedr-starter-set-preview-title' : 'sedr-starter-set-import-title',
    helpTopic: dialogHelpTopic('sedrImportHub'),
    className: isPreview ? 'sedr-starter-set-preview-dialog' : 'sedr-starter-set-import-dialog',
  });

  shell.body.innerHTML = isPreview
    ? `
    <p class="muted" id="sedr-starter-set-import-subtitle"></p>
    <div class="table-wrap sedr-starter-set-import__table-wrap">
      <table class="table sedr-starter-set-import__table">
        <thead>
          <tr>
            <th data-i18n="sedr.table.reqId"></th>
            <th data-i18n="sedr.table.title"></th>
            <th data-i18n="sedr.table.category"></th>
            <th data-i18n="sedr.table.description"></th>
          </tr>
        </thead>
        <tbody id="sedr-starter-set-import-rows"></tbody>
      </table>
    </div>
    <p class="error-text" id="sedr-starter-set-import-error" hidden></p>
  `
    : `
    <p class="muted" id="sedr-starter-set-import-subtitle"></p>
    <div class="sedr-starter-set-import__toolbar">
      <button type="button" class="button ghost" id="sedr-starter-set-select-all" data-i18n="sedr.import.selectAll"></button>
      <button type="button" class="button ghost" id="sedr-starter-set-select-none" data-i18n="sedr.import.selectNone"></button>
      <span class="muted" id="sedr-starter-set-selected-count"></span>
    </div>
    <div class="table-wrap sedr-starter-set-import__table-wrap">
      <table class="table sedr-starter-set-import__table">
        <thead>
          <tr>
            <th scope="col" class="sedr-starter-set-import__check-col">
              <input type="checkbox" id="sedr-starter-set-select-all-checkbox" aria-label="Select all" />
            </th>
            <th data-i18n="sedr.table.reqId"></th>
            <th data-i18n="sedr.table.title"></th>
            <th data-i18n="sedr.table.category"></th>
          </tr>
        </thead>
        <tbody id="sedr-starter-set-import-rows"></tbody>
      </table>
    </div>
    <p class="error-text" id="sedr-starter-set-import-error" hidden></p>
  `;

  const subtitleEl = shell.body.querySelector('#sedr-starter-set-import-subtitle') as HTMLParagraphElement;
  subtitleEl.textContent = options.setName;

  const importBtn = document.createElement('button');
  importBtn.type = 'button';
  importBtn.className = 'button primary';
  importBtn.id = 'sedr-starter-set-import-commit';
  importBtn.setAttribute('data-i18n', 'sedr.import.importSelected');
  importBtn.disabled = true;

  const closeActionBtn = document.createElement('button');
  closeActionBtn.type = 'button';
  closeActionBtn.className = 'button ghost';
  closeActionBtn.setAttribute('data-i18n', isPreview ? 'sedr.import.hubClose' : 'common.cancel');
  if (isPreview) {
    shell.actions.append(closeActionBtn);
  } else {
    shell.actions.append(importBtn, closeActionBtn);
  }

  const errorEl = shell.body.querySelector('#sedr-starter-set-import-error') as HTMLParagraphElement;
  const countEl = shell.body.querySelector('#sedr-starter-set-selected-count') as HTMLSpanElement | null;
  const tbody = shell.body.querySelector('#sedr-starter-set-import-rows') as HTMLTableSectionElement;
  const selectAllCheckbox = shell.body.querySelector('#sedr-starter-set-select-all-checkbox') as HTMLInputElement | null;

  let detail: SedrStarterSetDetail | null = null;
  let busy = false;

  const setError = (message: string | null) => {
    if (!message) {
      errorEl.hidden = true;
      errorEl.textContent = '';
      return;
    }
    errorEl.hidden = false;
    errorEl.textContent = message;
  };

  const setBusy = (value: boolean) => {
    busy = value;
    for (const button of shell.dialog.querySelectorAll('button')) {
      button.disabled = value;
    }
    for (const input of shell.dialog.querySelectorAll('input[type="checkbox"]')) {
      (input as HTMLInputElement).disabled = value;
    }
    if (!value && !isPreview) {
      updateSelectionUi();
    }
  };

  const getSelectedIds = (): Set<string> => {
    const ids = new Set<string>();
    if (!tbody) return ids;
    for (const input of tbody.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked')) {
      const id = input.dataset.itemId;
      if (id) ids.add(id);
    }
    return ids;
  };

  const updateSelectionUi = () => {
    if (isPreview || !countEl) return;
    const selected = getSelectedIds();
    const total = detail?.items.length ?? 0;
    countEl.textContent = t('sedr.import.selectedCount', { selected: selected.size, total });
    importBtn.disabled = busy || selected.size === 0;
    if (selectAllCheckbox && total > 0) {
      selectAllCheckbox.checked = selected.size === total;
      selectAllCheckbox.indeterminate = selected.size > 0 && selected.size < total;
    }
  };

  const setAllChecked = (checked: boolean) => {
    if (!tbody || isPreview) return;
    for (const input of tbody.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
      input.checked = checked;
    }
    updateSelectionUi();
  };

  const renderRows = () => {
    if (!detail || !tbody) return;
    tbody.replaceChildren();
    for (const item of detail.items) {
      const tr = document.createElement('tr');

      if (!isPreview) {
        const checkTd = document.createElement('td');
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = true;
        checkbox.dataset.itemId = item.id;
        checkbox.addEventListener('change', updateSelectionUi);
        checkTd.append(checkbox);
        tr.append(checkTd);
      }

      const codeTd = document.createElement('td');
      const code = document.createElement('code');
      code.textContent = item.requirementCode;
      codeTd.append(code);

      const titleTd = document.createElement('td');
      titleTd.textContent = item.title;

      const categoryTd = document.createElement('td');
      categoryTd.textContent = item.category;

      tr.append(codeTd, titleTd, categoryTd);

      if (isPreview) {
        const descriptionTd = document.createElement('td');
        descriptionTd.textContent = item.description?.trim() ? item.description : '—';
        tr.append(descriptionTd);
      }

      tbody.append(tr);
    }
    if (!isPreview) updateSelectionUi();
  };

  const refreshLocale = () => {
    applyI18n(shell.dialog);
    renderRows();
  };
  refreshLocale();
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: () => {
      if (!busy) close();
    },
  });

  closeActionBtn.addEventListener('click', () => {
    if (!busy) close();
  });

  if (!isPreview) {
    shell.body.querySelector('#sedr-starter-set-select-all')?.addEventListener('click', () => setAllChecked(true));
    shell.body.querySelector('#sedr-starter-set-select-none')?.addEventListener('click', () => setAllChecked(false));
    selectAllCheckbox?.addEventListener('change', () => setAllChecked(selectAllCheckbox.checked));

    importBtn.addEventListener('click', () => {
      if (busy || !detail || !options.roomId || !options.onDone) return;
      const selectedIds = getSelectedIds();
      if (selectedIds.size === 0) return;

      const rows = detail.items
        .filter((item) => selectedIds.has(item.id))
        .map((item) => ({
          requirementCode: item.requirementCode,
          title: item.title,
          description: item.description,
          category: item.category,
          ownerName: item.ownerName,
          testMethodFrequency: item.testMethodFrequency,
          status: 'PendingUpload',
        }));

      setBusy(true);
      setError(null);
      void (async () => {
        try {
          await sedrApi.importRequirements(options.roomId!, rows);
          close();
          options.onDone!();
        } catch (cause) {
          setBusy(false);
          setError(t(sedrErrorMessageKey(cause)));
        }
      })();
    });
  }

  document.body.append(shell.backdrop);

  void (async () => {
    try {
      detail = await sedrApi.getStarterSet(options.setId);
      renderRows();
    } catch {
      setError(t('status.error'));
      if (!isPreview) importBtn.disabled = true;
    }
  })();
}
